import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { AuthProvider } from "./context/AuthContext";
import { useAuth } from "./context/AuthContext";

// Componentes de Layout
import TopBarLayout from "./components/TopBarLayout";

// Componentes de Rutas Protegidas
import ProtectedRoute from "./components/routes/ProtectedRoute";

// Páginas Principales
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Profile from "./pages/Profile";
import Opportunities from "./pages/OpportunitiesList";
import OpportunityDetail from "./pages/OpportunityDetailPage";
import Investments from "./pages/Investments";
import Portfolio from "./pages/Portfolio";
import Messages from "./pages/Messages";
import Notifications from "./pages/Notifications";
import TestOpportunities from "./pages/TestOpportunities";
import AdminDebugPanel from "./components/AdminDebugPanel";
import AuthDebugger from "./components/AuthDebugger";
import UserAccessDiagnostic from "./components/UserAccessDiagnostic";
import UserSettingsPage from "./pages/UserSettingsPage";

// Páginas de Admin
import AdminPanel from "./pages/AdminPanel";
import AdminRoutes from "./routes/AdminRoutes";

// Páginas de Auth
import Register from "./pages/auth/UnifiedSignup";
import ForgotPassword from "./pages/PasswordReset";
import EmailVerification from "./pages/KYCWaitingApproval";

// Tema personalizado
const theme = createTheme({
    palette: {
        primary: {
            main: "#1976d2",
        },
        secondary: {
            main: "#dc004e",
        },
    },
    typography: {
        fontFamily: "\"Roboto\", \"Helvetica\", \"Arial\", sans-serif",
    },
});

/**
 * Componente principal de la aplicación
 */
const AppContent = () => {
    const { currentUser, loading } = useAuth();

    // Función de traducción simple
    const t = (key, fallback = key) => {
        const translations = {
            "menu.dashboard": "Dashboard",
            "menu.opportunities": "Opportunities",
            "menu.portfolio": "Portfolio",
            "menu.investments": "Investments",
            "menu.kyc": "KYC Verification",
            "menu.financialVerification": "Financial Verification",
            "menu.accountSettings": "Account Settings",
            "menu.closeAccount": "Close My Account",
            "menu.messages": "Messages",
            "menu.language": "Language",
            "menu.theme": "Theme",
            "menu.adminPanel": "Admin Panel",
            "menu.kycManagement": "KYC Management",
            "menu.agentes": "Agents",
            "menu.collections": "Collections",
            "menu.invest": "Invest",
            "menu.account": "My Account",
            "menu.support": "Support & Tools"
        };
        return translations[key] || fallback;
    };

    // Mostrar loading mientras se verifica autenticación
    if (loading) {
        return (
            <div style={{ 
                display: "flex", 
                justifyContent: "center", 
                alignItems: "center", 
                height: "100vh" 
            }}>
                <div>Cargando...</div>
            </div>
        );
    }

    return (
        <Router
            future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true
            }}
        >
            <Routes>
                {/* Rutas públicas */}
                <Route path="/login" element={!currentUser ? <Login /> : <Navigate to="/dashboard" />} />
                <Route path="/register" element={!currentUser ? <Register /> : <Navigate to="/dashboard" />} />
                <Route path="/forgot-password" element={!currentUser ? <ForgotPassword /> : <Navigate to="/dashboard" />} />
                <Route path="/email-verification" element={<EmailVerification />} />

                {/* Rutas protegidas principales */}
                <Route path="/" element={
                    <ProtectedRoute>
                        <TopBarLayout t={t}>
                            <Dashboard />
                        </TopBarLayout>
                    </ProtectedRoute>
                } />

                <Route path="/dashboard" element={
                    <ProtectedRoute>
                        <TopBarLayout t={t}>
                            <Dashboard />
                        </TopBarLayout>
                    </ProtectedRoute>
                } />

                <Route path="/profile" element={
                    <ProtectedRoute>
                        <TopBarLayout t={t}>
                            <Profile />
                        </TopBarLayout>
                    </ProtectedRoute>
                } />

                <Route path="/opportunities" element={
                    <ProtectedRoute>
                        <TopBarLayout t={t}>
                            <Opportunities />
                        </TopBarLayout>
                    </ProtectedRoute>
                } />

                <Route path="/opportunity/:id" element={
                    <ProtectedRoute>
                        <TopBarLayout t={t}>
                            <OpportunityDetail />
                        </TopBarLayout>
                    </ProtectedRoute>
                } />

                <Route path="/investments" element={
                    <ProtectedRoute>
                        <TopBarLayout t={t}>
                            <Investments />
                        </TopBarLayout>
                    </ProtectedRoute>
                } />

                <Route path="/portfolio" element={
                    <ProtectedRoute>
                        <TopBarLayout t={t}>
                            <Portfolio />
                        </TopBarLayout>
                    </ProtectedRoute>
                } />

                <Route path="/messages" element={
                    <ProtectedRoute>
                        <TopBarLayout t={t}>
                            <Messages />
                        </TopBarLayout>
                    </ProtectedRoute>
                } />

                <Route path="/notifications" element={
                    <ProtectedRoute>
                        <TopBarLayout t={t}>
                            <Notifications />
                        </TopBarLayout>
                    </ProtectedRoute>
                } />

                {/* Ruta de prueba */}
                <Route path="/test-opportunities" element={
                    <ProtectedRoute>
                        <TopBarLayout t={t}>
                            <TestOpportunities />
                        </TopBarLayout>
                    </ProtectedRoute>
                } />

                {/* Ruta de debug de administración */}
                <Route path="/admin-debug" element={
                    <ProtectedRoute>
                        <TopBarLayout t={t}>
                            <AdminDebugPanel />
                        </TopBarLayout>
                    </ProtectedRoute>
                } />

                {/* Ruta de debug de autenticación */}
                <Route path="/auth-debug" element={
                    <ProtectedRoute>
                        <TopBarLayout t={t}>
                            <AuthDebugger />
                        </TopBarLayout>
                    </ProtectedRoute>
                } />

                {/* Ruta de diagnóstico específico del usuario */}
                <Route path="/user-diagnostic" element={
                    <ProtectedRoute>
                        <TopBarLayout t={t}>
                            <UserAccessDiagnostic />
                        </TopBarLayout>
                    </ProtectedRoute>
                } />

                {/* Rutas de Administración Modular */}
                <Route path="/admin/*" element={
                    <ProtectedRoute>
                        <AdminRoutes />
                    </ProtectedRoute>
                } />

                {/* Ruta de admin legacy (para compatibilidad) */}
                <Route path="/admin-panel" element={
                    <ProtectedRoute>
                        <TopBarLayout t={t}>
                            <AdminPanel />
                        </TopBarLayout>
                    </ProtectedRoute>
                } />

                {/* Ruta de configuración del usuario */}
                <Route path="/settings" element={
                    <ProtectedRoute>
                        <TopBarLayout t={t}>
                            <UserSettingsPage />
                        </TopBarLayout>
                    </ProtectedRoute>
                } />

                {/* Ruta por defecto */}
                <Route path="*" element={<Navigate to="/dashboard" />} />
            </Routes>
        </Router>
    );
};

/**
 * Componente principal de la aplicación
 */
const App = () => {
    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <AuthProvider>
                <AppContent />
            </AuthProvider>
        </ThemeProvider>
    );
};

export default App;